


























































































import Vue from 'vue'
import Component from 'vue-class-component'
import { AccessCode, AccessCodeData, UserRole } from '@/core/Types'
import appManager from '@/services/AppManager'
import * as db from '@/core/Database'
import { FieldValue, QuerySnapshot, Timestamp } from '@/extern/FirebaseTypes'
import Spinner from '@/components/Spinner.vue'
import { createPseudoRandomString, getDateText } from '@/model/Helpers'
import CreateAccessCodeDialog from '@/components/accesscodes/CreateAccessCodeDialog.vue'
import EditAccessCodeDialog from '@/components/accesscodes/EditAccessCodeDialog.vue'
import * as api from '@/services/ApiService'

@Component({
    components: {
        Spinner,
        CreateAccessCodeDialog,
        EditAccessCodeDialog,
    }
})
export default class AccessCodesView extends Vue {
    // users
    accessCodeHeaders = [
        { text: 'Created', value: 'data.createdTimestamp' },
        { text: 'Expires', value: 'data.accessEndTimestamp' },
        { text: 'Name', value: 'data.name', align: 'start' },
        { text: 'Access Code', value: 'id', align: 'end' }
    ]
    selectedAccessCodes: AccessCode[] = []

    // access codes
    accessCodesCounter: number = 0 // simple way to force a refresh when things change
    accessCodes: AccessCode[] = []
    accessCodesUnsubscriber: VoidFunction|null = null
    visibleAccessCodeIds = new Set<string>()

    // create dialog
    isCreateAccessCodeDialogVisible: boolean = false
    createAccessCodeDialogCounter: number = 0 // simple way to reset the dialog each time it is opened
    initialAccessCodeId: string = ''

    // edit dialog
    isEditAccessCodeDialogVisible: boolean = false
    editAccessCode: AccessCode|null = null
    editAccessCodeDialogCounter: number = 0 // simple way to reset the dialog before opening each time

    // delete dialog
    isDeleteAccessCodesDialogVisible: boolean = false
    isDeletingAccessCodes: boolean = false

    // noinspection JSUnusedGlobalSymbols
    async created() {
        const user = appManager.userManager.state?.user
        if (user && user.data.role === UserRole.ADMIN) {
            this.accessCodesUnsubscriber = db.streamAccessCodes(
                { orderByCreatedTimestampDescending: true },
                snapshot => this.onAccessCodesReceived(snapshot),
                error => this.onAccessCodesError(error)
            )
        }
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        if (this.accessCodesUnsubscriber)
            this.accessCodesUnsubscriber()
    }

    onAccessCodesReceived(snapshot: QuerySnapshot) {
        this.accessCodes = snapshot.docs
            .filter(doc => doc.exists)
            .map(doc => {
                return {
                    id: doc.id,
                    data: doc.data() as AccessCodeData
                }
            })

        this.accessCodesCounter += 1
    }

    onAccessCodesError(error: Error) {
        console.error(`Error receiving access codes: ${error}`)
    }

    getAccessCodeName(accessCode: AccessCode|undefined): string {
        return accessCode?.data?.name || ''
    }

    getDateTextLocal(timestamp: Timestamp|FieldValue|undefined) {
        return timestamp ? getDateText(timestamp) : '---'
    }

    getAccessCodeId(accessCode: AccessCode): string|undefined {
        return accessCode?.id || ''
    }

    isAccessCodeVisible(accessCode: AccessCode): boolean {
        return this.visibleAccessCodeIds.has(accessCode.id)
    }

    async onShowAccessCodePressed(accessCode: AccessCode) {
        this.visibleAccessCodeIds.add(accessCode.id)
        this.accessCodesCounter += 1
    }

    onAddAccessCodePressed() {
        this.createAccessCodeDialogCounter += 1
        this.initialAccessCodeId = createPseudoRandomString(10)
        this.isCreateAccessCodeDialogVisible = true
    }

    onCloseAddAccessCodeDialogRequested() {
        this.isCreateAccessCodeDialogVisible = false
    }

    onEditSelectedAccessCodePressed() {
        if (this.selectedAccessCodes.length > 0) {
            this.editAccessCodeDialogCounter += 1
            this.editAccessCode = this.selectedAccessCodes[0]
            this.isEditAccessCodeDialogVisible = true
        }
    }

    onCloseEditAccessCodeDialogRequested() {
        this.isEditAccessCodeDialogVisible = false
        this.selectedAccessCodes = []
    }

    onDeleteSelectedAccessCodesPressed() {
        if (this.selectedAccessCodes.length > 0) {
            this.isDeletingAccessCodes = false
            this.isDeleteAccessCodesDialogVisible = true
        }
    }

    async onDeleteAccessCodesConfirmed() {
        this.isDeletingAccessCodes = true

        try {
            const promises = this.selectedAccessCodes.map(accessCode => {
                api.deleteAccessCode({ accessCodeId: accessCode.id })
            })
            await Promise.all(promises)
        } catch (error) {
            console.error(`Error Deleting Access Codes: ${error}`)
        }

        this.selectedAccessCodes = []
        this.isDeletingAccessCodes = false
        this.isDeleteAccessCodesDialogVisible = false
    }
}
